import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useFetcher, useLocation } from '@remix-run/react';
import { Search, MapPin, Sparkles } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { useDebounce } from '~/utils/hooks/useDebounce';
import { cn } from '~/lib/utils';
import { urlPattern } from '~/utils/urlPattern';

const categories = [
  { label: 'Consulta Médica', value: 'alquiler-consulta-medica' },
  { label: 'Talleres', value: 'taller' },
  { label: 'Sala Formacion | Reuniones', value: 'sala-de-formacion' },
  { label: 'Salas de Yoga | Gym | Bailes | Retiros', value: 'sala-de-yoga' },
  { label: 'Coworking | Estudio', value: 'estudio' },
  { label: 'Obradores | Cocinas', value: 'obradores' },
  { label: 'Naves', value: 'naves-industriales' },
  { label: 'Exposiciones', value: 'sala-de-exposiciones' },
  { label: 'Locales para fiestas', value: 'locales-para-fiestas' },
  { label: 'Oficinas | Despacho', value: 'despacho' },
  { label: 'Estudio Fotográfico | Localizaciones', value: 'estudios-fotograficos' },
  { label: 'Locales', value: 'locales' },
  { label: 'Locales de ensayo', value: 'locales-de-ensayo' },
  { label: 'Cabinas | Salones de belleza', value: 'cabinas-salones-de-belleza' },
  { label: 'Sala multidisciplinar | Espacio', value: 'sala-multidisciplinar' },
  { label: 'Sala para Terapias', value: 'sala-para-terapias' },
  { label: 'Tiendas', value: 'tienda' },
  { label: 'Almacén | Trastero', value: 'almacen' },
  { label: 'Oficina virtual', value: 'oficina-virtual' },
];

type CitySuggestion = {
  slug: string;
  name: string;
  countryCode: string | null;
  workspaceCount: number;
};

type CategoryCombo = {
  citySlug: string;
  cityName: string;
  categorySlug: string;
  categoryName: string;
  workspaceCount: number;
};

type SuggestionsData = {
  cities: CitySuggestion[];
  cityCategoryCombos: CategoryCombo[];
};

export default function SearchBar2() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const debouncedSearch = useDebounce(searchTerm, 300);
  const navigate = useNavigate();
  const location = useLocation();
  const fetcher = useFetcher<SuggestionsData>();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    setShowLeftFade(scrollLeft > 10);
    setShowRightFade(scrollLeft < maxScroll - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    el?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const handleCategorySelect = (categoryValue: string) => {
    const parsed = urlPattern.parse(location.pathname);
    // If we're on a non-city route (like /espacios/buscar), default to 'all'
    const city = parsed.city && parsed.city !== 'buscar' ? parsed.city : 'all';
    navigate(urlPattern.build({ category: categoryValue, city }));
  };

  const isCategoryActive = (categoryValue: string) => {
    return urlPattern.isCategoryActive(location.pathname, categoryValue);
  };

  // Build flat list of selectable items for keyboard nav
  const getSelectableItems = useCallback((): Array<
    | { type: 'city'; data: CitySuggestion }
    | { type: 'combo'; data: CategoryCombo }
    | { type: 'search' }
  > => {
    const items: Array<
      | { type: 'city'; data: CitySuggestion }
      | { type: 'combo'; data: CategoryCombo }
      | { type: 'search' }
    > = [];

    if (fetcher.data?.cities) {
      for (const city of fetcher.data.cities) {
        items.push({ type: 'city', data: city });
      }
    }
    if (fetcher.data?.cityCategoryCombos) {
      for (const combo of fetcher.data.cityCategoryCombos) {
        items.push({ type: 'combo', data: combo });
      }
    }
    // Always add "search for..." option at the end
    if (searchTerm.trim().length >= 2) {
      items.push({ type: 'search' });
    }

    return items;
  }, [fetcher.data, searchTerm]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions when debounced search changes
  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      fetcher.load(`/resources/search-suggestions?q=${encodeURIComponent(debouncedSearch)}`);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setActiveIndex(-1);
  }, [debouncedSearch]);

  const handleCitySelect = (city: CitySuggestion) => {
    navigate(`/espacios/${city.slug}`);
    setSearchTerm(city.name);
    setShowSuggestions(false);
  };

  const handleComboSelect = (combo: CategoryCombo) => {
    navigate(`/espacios/${combo.citySlug}/${combo.categorySlug}`);
    setSearchTerm(`${combo.cityName} ${combo.categoryName}`);
    setShowSuggestions(false);
  };

  const handleFreeTextSearch = () => {
    if (!searchTerm.trim()) return;
    navigate(`/espacios/buscar?q=${encodeURIComponent(searchTerm.trim())}`);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = getSelectableItems();
    if (!showSuggestions || items.length === 0) {
      if (e.key === 'Enter') {
        handleFreeTextSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev < items.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => (prev > 0 ? prev - 1 : items.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < items.length) {
          const item = items[activeIndex];
          if (item.type === 'city') handleCitySelect(item.data);
          else if (item.type === 'combo') handleComboSelect(item.data);
          else handleFreeTextSearch();
        } else {
          handleFreeTextSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
    }
  };

  const hasSuggestions =
    (fetcher.data?.cities?.length ?? 0) > 0 ||
    (fetcher.data?.cityCategoryCombos?.length ?? 0) > 0;

  const isLoading = fetcher.state === 'loading';

  // Track running index for keyboard nav across sections
  let itemIndex = 0;

  return (
    <div className="w-full max-w-3xl mx-auto px-4">
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={inputRef}
                className="h-12 w-full rounded-lg border bg-white px-11 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Busca por ciudad, categoría o palabra clave..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchTerm.length >= 2 && hasSuggestions) {
                    setShowSuggestions(true);
                  }
                }}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={showSuggestions}
                aria-haspopup="listbox"
                aria-autocomplete="list"
              />
            </div>

            {showSuggestions && searchTerm.length >= 2 && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border shadow-lg z-50"
                role="listbox"
              >
                <div className="max-h-[400px] overflow-auto">
                  {isLoading && !hasSuggestions ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      Buscando...
                    </div>
                  ) : (
                    <>
                      {/* Cities section */}
                      {fetcher.data?.cities && fetcher.data.cities.length > 0 && (
                        <div className="p-2">
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Ciudades
                          </div>
                          {fetcher.data.cities.map((city) => {
                            const currentIndex = itemIndex++;
                            return (
                              <div
                                key={`city-${city.slug}`}
                                className={cn(
                                  'flex items-center gap-2 px-2 py-2.5 rounded-md cursor-pointer transition-colors',
                                  activeIndex === currentIndex
                                    ? 'bg-accent'
                                    : 'hover:bg-accent/50'
                                )}
                                onClick={() => handleCitySelect(city)}
                                onMouseEnter={() => setActiveIndex(currentIndex)}
                                role="option"
                                aria-selected={activeIndex === currentIndex}
                              >
                                <MapPin className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                <span className="flex-1 font-medium">{city.name}</span>
                                {city.countryCode && (
                                  <span className="text-xs text-muted-foreground">
                                    ({city.countryCode})
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {city.workspaceCount} {city.workspaceCount === 1 ? 'espacio' : 'espacios'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* City + Category combos section */}
                      {fetcher.data?.cityCategoryCombos && fetcher.data.cityCategoryCombos.length > 0 && (
                        <div className="p-2 border-t">
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Sugerencias
                          </div>
                          {fetcher.data.cityCategoryCombos.map((combo) => {
                            const currentIndex = itemIndex++;
                            return (
                              <div
                                key={`combo-${combo.citySlug}-${combo.categorySlug}`}
                                className={cn(
                                  'flex items-center gap-2 px-2 py-2.5 rounded-md cursor-pointer transition-colors',
                                  activeIndex === currentIndex
                                    ? 'bg-accent'
                                    : 'hover:bg-accent/50'
                                )}
                                onClick={() => handleComboSelect(combo)}
                                onMouseEnter={() => setActiveIndex(currentIndex)}
                                role="option"
                                aria-selected={activeIndex === currentIndex}
                              >
                                <Sparkles className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                <span className="flex-1">
                                  <span className="font-medium">{combo.cityName}</span>
                                  <span className="mx-1.5 text-muted-foreground">·</span>
                                  <span>{combo.categoryName}</span>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {combo.workspaceCount} {combo.workspaceCount === 1 ? 'espacio' : 'espacios'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Free text search fallback */}
                      {searchTerm.trim().length >= 2 && (
                        <div className="p-2 border-t">
                          {(() => {
                            const currentIndex = itemIndex++;
                            return (
                              <div
                                className={cn(
                                  'flex items-center gap-2 px-2 py-2.5 rounded-md cursor-pointer transition-colors',
                                  activeIndex === currentIndex
                                    ? 'bg-accent'
                                    : 'hover:bg-accent/50'
                                )}
                                onClick={handleFreeTextSearch}
                                onMouseEnter={() => setActiveIndex(currentIndex)}
                                role="option"
                                aria-selected={activeIndex === currentIndex}
                              >
                                <Search className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  Buscar &quot;{searchTerm.trim()}&quot;
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <Button
            size="lg"
            onClick={handleFreeTextSearch}
            className="hidden sm:flex"
          >
            Buscar
          </Button>
          <Button
            size="icon"
            className="h-12 w-12 sm:hidden"
            onClick={handleFreeTextSearch}
          >
            <Search className="h-5 w-5" />
          </Button>
        </div>

        {/* Category slider */}
        <div className="relative mt-4">
          {showLeftFade && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white p-3 shadow-md border border-gray-200 transition-all hover:scale-105 hover:shadow-lg"
              aria-label="Scroll left"
            >
              <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {showRightFade && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white p-3 shadow-md border border-gray-200 transition-all hover:scale-105 hover:shadow-lg"
              aria-label="Scroll right"
            >
              <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth py-2 px-12"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => handleCategorySelect(category.value)}
                className={cn(
                  'flex shrink-0 items-center gap-2 rounded-full border px-6 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap',
                  isCategoryActive(category.value)
                    ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:shadow-sm'
                )}
              >
                {category.label}
              </button>
            ))}
          </div>

          <p className="my-4 text-center text-sm text-gray-500">Desliza para ver más categorías →</p>
        </div>
      </div>
    </div>
  );
}
